import type { GalleryImage } from '@/components/image-gallery-custom/types';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { PimCertificationsList } from './pim-certifications-list';
import { ProductLabelGallery } from './product-label-gallery';
import { formatDateForLocale } from 'src/lib/date-utils';
import type { SCAPIShopperProductsSchemas } from 'src/types';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { toGalleryThumbnailSrc } from '@/lib/images/gallery-sizes';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useProductPriceWeightDisplay } from 'src/components/product-utils';
import { resolvePimCertifications } from 'src/lib/pim-certifications';
import { isVariantProduct } from '@/lib/product-utils';
import { PRODUCT_IMAGE_VIEW_TYPES } from '@/lib/product-image-view-types';

type PrefixedProductCustomAttributes = {
    [K in keyof ProductCustomAttributes as K extends string ? `c_${K}` : never]?: ProductCustomAttributes[K];
};
type Product = SCAPIShopperProductsSchemas['Product'] & PrefixedProductCustomAttributes;
type ProductCustomAttributeKey = keyof PrefixedProductCustomAttributes;
type ProductLabelAttributeKey = 'id' | ProductCustomAttributeKey;

const LABEL_ATTRIBUTE_ROWS = [
    'id',
    'c_pimEtiNetWeight',
    'c_pimDrainedNetWeightDesc',
    'c_pimBrandName',
    'c_pimMadeIn',
    'c_pimFlavor',
    'c_pimOtherLifestyles',
    'c_pimMilk',
    'c_pimSeasoning',
    'c_pimBreedingType',
    'c_pimAnimalCountryCode',
    'c_pimFarmIstatCode',
    'c_pimFarmProvinceCode',
    'c_pimEggSizeClass',
    'c_pimLayingFarmId',
    'c_pimNrOfPages',
    'c_pimPublicationDate',
    'c_pimPublisher',
    'c_pimAuthorPublication',
    'c_pimPublicationLanguage',
    'c_pimIsbn',
    'c_pimAlchoolVolume',
    'c_pimCatQualityOf',
    'c_pimCalibQualityOf',
    'c_pimOriginQualityOf',
    'c_pimVarietyQualityOf',
    'c_pimZonaFAOQuality',
    'c_pimLatinNameQuality',
    'c_pimRaisedQuality',
    'c_pimFishingGearQuality',
    'c_pimFreshOrNotQuality',
    'c_pimBornInQuality',
    'c_pimRaisedInQuality',
    'c_pimSlaughteredInQuality',
    'c_pimButchersStampQuality',
    'c_pimDissectionerInQuality',
    'c_pimDissectionerStampQuality',
    'c_pimMinimumGuaranteedMaturity',
    'c_pimSalesName',
    'c_pimFabricComposition',
    'c_pimWashingInstructions',
    'c_pimGeographicalArea',
    'c_pimWineRegion',
    'c_pimWineVariety',
    'c_pimWineVintageAlchoolVolume',
    'c_pimWineVintageAllergens',
    'c_pimWineVintageVariety',
] as const satisfies readonly ProductLabelAttributeKey[];
const PACKAGING_BOOLEAN_LABEL_ATTRIBUTE_ROWS = [
    'c_pimPlasticFree',
    'c_pimCompostable',
] as const satisfies readonly ProductCustomAttributeKey[];
const CONSERVATION_CONSUMPTION_LABEL_ATTRIBUTE_ROWS = [
    'c_pimPreservation',
    'c_pimConsumptionModeQuality',
] as const satisfies readonly ProductCustomAttributeKey[];

function formatProductAttributeValue(value: unknown) {
    if (Array.isArray(value)) {
        return value.join(', ');
    }

    if (value instanceof Date) {
        return value.toISOString().slice(0, 10);
    }

    return String(value);
}

function labelImagesFrom(source: Product | undefined): GalleryImage[] {
    const raw =
        source?.imageGroups?.find((imageGroup) => imageGroup.viewType === PRODUCT_IMAGE_VIEW_TYPES.LAB)?.images || [];
    return raw.map((image) => {
        const src = image.disBaseLink || image.link || '';
        return {
            src,
            alt: image.alt,
            thumbSrc: toGalleryThumbnailSrc(src),
        };
    });
}

export default function ProductTabsLabel({
    product,
    labelImageProduct,
}: {
    product: Product;
    /** Prefer this SKU's `viewType: label` images; fall back to `product` when empty. */
    labelImageProduct?: Product;
}) {
    const { currency } = useSite();
    const { perUnitLine } = useProductPriceWeightDisplay(product, currency);
    const { t, i18n } = useTranslation('product');
    const rows = useMemo(() => {
        return LABEL_ATTRIBUTE_ROWS.filter(
            (key) => product[key] && !(key === 'c_pimAlchoolVolume' && product.c_pimWineVintageAlchoolVolume)
        ).map((key) => {
            let value = formatProductAttributeValue(product[key]);
            if (key === 'c_pimPublicationDate') {
                value = formatDateForLocale(value, i18n.language) ?? '';
            }
            if (key === 'id') {
                // Show the raw ERP code (the catalog id carries the site productIdPrefix);
                // vintage variants share the master's code.
                if (product.c_erpProductCode) {
                    value = product.c_erpProductCode;
                } else if (isVariantProduct(product) && product.master) {
                    value = product.master.masterId;
                }
            }
            return {
                key,
                label: t(`pimLabelAttributes.${key}` as never),
                value,
            };
        });
    }, [product, t, i18n]);

    const packagingBooleanRows = useMemo(() => {
        return PACKAGING_BOOLEAN_LABEL_ATTRIBUTE_ROWS.filter((key) => product[key]).map((key) => ({
            key,
            label: t(`pimLabelAttributes.${key}` as never),
        }));
    }, [product, t]);

    const packagingRows = useMemo(() => {
        const parts = product.c_pimWasteDisposal;
        if (!Array.isArray(parts)) {
            return [];
        }

        return parts.map((part) => part?.trim()).filter((part): part is string => Boolean(part));
    }, [product]);

    const conservationConsumptionRows = useMemo(() => {
        return CONSERVATION_CONSUMPTION_LABEL_ATTRIBUTE_ROWS.filter((key) => product[key]).map((key) => {
            return {
                key,
                label: `pimLabelAttributes.${key}` as unknown as string,
                value: formatProductAttributeValue(product[key]),
            };
        });
    }, [product]);

    const labelGalleryImages = useMemo((): GalleryImage[] => {
        const preferred = labelImagesFrom(labelImageProduct);
        return preferred.length > 0 ? preferred : labelImagesFrom(product);
    }, [labelImageProduct, product]);

    const imageAltFallback = resolveImageAlt({ name: product.name, fallback: t('imageAlt') || 'Product Image' });
    const certifications = useMemo(() => {
        if (product.c_pimCertifications) {
            return resolvePimCertifications(product.c_pimCertifications);
        }
        return [];
    }, [product.c_pimCertifications]);

    return (
        <div className="grid grid-cols-1 gap-4 lg:gap-8 lg:grid-cols-[3fr_2fr] lg:grid-rows-[auto_1fr]">
            <div>
                <section className="rounded bg-sand-95 px-4 py-4">
                    <ul className="m-0 divide-y divide-neutral-80">
                        {rows.map(({ key, label, value }) => (
                            <li key={key} className="flex gap-4 py-2 first:pt-0 last:pb-0 items-start">
                                <span className="m-0 flex-1 min-w-0 text-base font-normal leading-6 tracking-[0.01em] text-neutral-40 truncate">
                                    {label}
                                </span>
                                <span className="m-0 shrink-0 max-w-[55%] text-base font-normal leading-6 tracking-[0.01em] text-neutral-10 break-words">
                                    {value || <i className="text-neutral-40 text-[10px]">{key}</i>}
                                </span>
                            </li>
                        ))}
                        {perUnitLine && (
                            <li className="flex gap-4 py-2 first:pt-0 last:pb-0 items-start">
                                <span className="m-0 flex-1 min-w-0 text-base font-normal leading-6 tracking-[0.01em] text-neutral-40 truncate">
                                    {t('pimLabelAttributes.pricePerUnit')}
                                </span>
                                <span className="m-0 shrink-0 max-w-[55%] text-base font-normal leading-6 tracking-[0.01em] text-neutral-10 break-words">
                                    {perUnitLine}
                                </span>
                            </li>
                        )}
                        {certifications.length > 0 && (
                            <li key={'certifications'} className="flex gap-4 py-2 first:pt-0 last:pb-0 items-start">
                                <span className="m-0 flex-1 min-w-0 text-base font-normal leading-6 tracking-[0.01em] text-neutral-40 truncate">
                                    {t('pimLabelAttributes.c_pimCertifications')}
                                </span>
                                <PimCertificationsList certifications={certifications} />
                            </li>
                        )}
                    </ul>
                </section>

                {(packagingBooleanRows.length > 0 || packagingRows.length > 0) && (
                    <section className="pb-6">
                        <h2 className="body-copy-l-bold mb-4 mt-6">{t('pimLabelAttributes.c_pimPackaging')}</h2>
                        <div className="w-fit space-y-1">
                            {packagingBooleanRows.map(({ key, label }) => (
                                <p key={key} className="body-copy-l-bold">
                                    {label}
                                </p>
                            ))}
                            {packagingRows.map((html) => (
                                <div
                                    key={html}
                                    className="body-copy-l w-fit max-w-full"
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: html }}
                                />
                            ))}
                        </div>
                    </section>
                )}
                {conservationConsumptionRows?.length > 0 && (
                    <section className="border-t border-neutral-80 py-3">
                        {conservationConsumptionRows?.map(({ key, value }) => (
                            <p key={key} className="body-copy-l lg:row-span-1">
                                {value}
                            </p>
                        ))}
                    </section>
                )}
                {product.c_pimDisclaimerEtichetta && (
                    <section className="border-t border-neutral-80 pt-6">
                        <p className="body-copy-s text-neutral-40 lg:row-span-1">{product.c_pimDisclaimerEtichetta}</p>
                    </section>
                )}
            </div>
            <div className="max-lg:mt-2 lg:row-span-2">
                <ProductLabelGallery images={labelGalleryImages} imageAltFallback={imageAltFallback} />
            </div>
        </div>
    );
}
